import { nanoid } from 'nanoid'
import { omit } from 'ply-utils'
import { Deferred } from 'ply-utils/Deferred'
import * as React from 'react'
import { immer } from 'zustand/middleware/immer'
import { useShallow } from 'zustand/shallow'
import { createWithEqualityFn as create } from 'zustand/traditional'

export type Status = 'progress' | 'success' | 'error'

export interface TaskUpdaters {
	addError(message: string): void
	updateStatus(status: Status, message?: string): void
	updateProgress(progress: number): Status
}

export type TaskProps<Payload> = React.PropsWithChildren<{
	id: string
	payload: Payload
	updaters: TaskUpdaters
}>

export interface ITaskType {
	Component: <T = unknown>(props: TaskProps<T>) => React.ReactElement
}

export interface Task {
	id: string
	type: string
	progress: number
	status: 'idle' | 'progress' | 'success' | 'error'
	payload: unknown
	metadata?: { errors: string[] }
	deferred: Deferred<void>
}

export interface ProviderState {
	types: Record<string, ITaskType>
	//
	visible: boolean
	//
	tasksOrder: string[]
	tasks: Record<string, Task>
	//
	registerType(name: string, type: ITaskType): void
	//
	toggle(): void
	//
	create<Payload = unknown>(type: string, payload: Payload): Promise<void>
	getUpdaters(id: string): TaskUpdaters
	//
	clearAll(): void
}

export const makeCreateStore = () => {
	const store = create<ProviderState>()(
		immer((set, get) => ({
			types: {},
			//
			visible: false,
			//
			tasksOrder: [],
			tasks: {},
			//
			registerType(name: string, type: ITaskType) {
				set((state) => {
					state.types[name] = type
				})
			},

			create<Payload = unknown>(type: string, payload: Payload) {
				if (!get().types[type]) {
					throw Error(`invalid task type '${type}'`)
				}

				const deferred = new Deferred<void>()

				set((state) => {
					const id = nanoid()

					state.tasks[id] = {
						id,
						type,
						payload,
						progress: 0,
						status: 'idle',
						deferred,
					}
					state.tasksOrder.push(id)
				})

				return deferred.promise
			},

			getUpdaters(id: string) {
				function updateStatus(
					status: 'progress' | 'success' | 'error',
					message?: string,
				) {
					set((state) => {
						const task = state.tasks[id]

						if (!task) {
							return
						}

						if (message) {
							task.metadata = task.metadata ?? { errors: [] }
							task.metadata.errors.unshift(message)
						}

						if (task.status === 'error') {
							// task.deferred.reject(
							//     new Error(
							//         `ply-ux/Tasks makeCreateStore error: '${message ?? ''}'`,
							//     ),
							// )
							task.deferred.resolve()
							return
						}

						if (status === 'success') {
							if (state.visible) {
								task.status = status
							} else {
								state.tasks = omit(state.tasks, id)
								state.tasksOrder = state.tasksOrder.filter((t) => t !== id)
							}

							task.deferred.resolve()

							return
						}

						task.status = status
					})
				}

				function addError(message: string) {
					set((state) => {
						const task = state.tasks[id]

						if (!task) {
							return
						}

						task.metadata = task.metadata ?? { errors: [] }
						task.metadata.errors.unshift(message)
					})
				}

				return {
					addError,

					updateStatus,

					updateProgress(progress: number) {
						set((state) => {
							const task = state.tasks[id]
							if (!task) {
								return
							}

							task.progress = progress
						})

						const task = get().tasks[id]

						if (progress === 100) {
							const hasErrors = Boolean(task?.metadata?.errors?.length)

							updateStatus(hasErrors ? 'error' : 'success')
						}

						return (get().tasks[id]?.status ?? 'success') as Status
					},
				}
			},

			toggle() {
				set((state) => {
					state.visible = !state.visible
				})
			},

			clearAll() {
				set((state) => {
					state.tasks = {}
					state.tasksOrder = []
					state.visible = false
				})
			},
		})),
	)

	return store
}

const Context = React.createContext<ReturnType<typeof makeCreateStore> | null>(
	null,
)

const Provider = ({ children }) => {
	const [store] = React.useState(() => {
		return makeCreateStore()
	})

	return <Context.Provider value={store}>{children}</Context.Provider>
}

function useTasks<U>(selector: (state: ProviderState) => U): U {
	const useStore = React.useContext(Context)

	if (!useStore) {
		throw Error()
	}

	return useStore(useShallow(selector))
}

export { Provider, useTasks }
